import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Loader2, Search, ChevronLeft, ChevronRight, X, Filter } from 'lucide-react';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Image URL helper function - HTTPS uyumlu
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // Production'da HTTPS kullan
  if (process.env.NODE_ENV === 'production') {
    // HTTPS ile başlamıyorsa HTTPS ekle
    if (imagePath.startsWith('http://')) {
      return imagePath.replace('http://', 'https://');
    }
    // HTTPS ile başlıyorsa direkt döndür
    if (imagePath.startsWith('https://')) {
      return imagePath;
    }
    // Relative path ise HTTPS domain ile birleştir
    if (imagePath.startsWith('/')) {
      return `https://leasertech.com${imagePath}`;
    }
    return `https://leasertech.com/${imagePath}`;
  }
  
  // Development'ta localhost kullan
  const API_BASE_URL = 'http://localhost:5000';
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${API_BASE_URL}${imagePath}`;
};

// Loading Button Component
const LoadingButton = ({ loading, children, ...props }) => (
  <button
    {...props}
    disabled={loading}
    className={`flex items-center justify-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
      loading
        ? 'bg-gray-400 cursor-not-allowed'
        : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
    }`}
  >
    {loading && <Loader2 className="animate-spin h-4 w-4 mr-2" />}
    {children}
  </button>
);

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const isProcessingImages = useRef(false);
  const searchInputRef = useRef(null);
  
  // Pagination ve arama state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    categories: [],
    subcategory: '',
    brand: '',
    sku: '',
    stock: '',
    min_stock: '5',
    images: [],
    specifications: [],
    tags: [],
    is_active: true,
    is_featured: false,
    weight: '',
    dimensions_length: '',
    dimensions_width: '',
    dimensions_height: '',
    shipping_free: false,
    shipping_cost: '0',
    estimated_delivery: '3-5 iş günü'
  });

  const categories = [
    'Şarj Aleti',
    'Powerbank', 
    'Kulaklık',
    'Hoparlör',
    'Otomobil Ürünleri',
    'Telefon Tutacakları',
    'Projeksiyon Cihazları',
    'Bilgisayar Aksesuarları',
    'Telefon Aksesuarları',
    'Diğer'
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, sortBy, sortOrder, categoryFilter, statusFilter, stockFilter]);

  const loadProducts = async () => {
    try {
      setListLoading(true);

      const response = await productsAPI.getAdminProducts({
        page: currentPage,
        limit: 50,
        search: debouncedSearch,
        sortBy,
        sortOrder,
        category: categoryFilter,
        status: statusFilter,
        stockStatus: stockFilter
      });

      if (response.data?.products && response.data?.pagination) {
        setProducts(response.data.products);
        setTotalPages(response.data.pagination.totalPages);
        setTotalProducts(response.data.pagination.totalProducts);
      } else {
        toast.error('Ürünler yüklenirken format hatası');
      }
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setListLoading(false);
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'categories') {
      // Kategori seçimi için özel işlem
      const categoryValue = e.target.value;
      setFormData(prev => ({
        ...prev,
        categories: prev.categories.includes(categoryValue)
          ? prev.categories.filter(cat => cat !== categoryValue)
          : [...prev.categories, categoryValue]
      }));
    } else if (name === 'images') {
      // Çift çağrıları engelle
      if (isProcessingImages.current) {
        console.log('Already processing images, skipping...');
        return;
      }
      
      isProcessingImages.current = true;
      
      // Görsel yükleme için özel işlem
      const files = Array.from(e.target.files);
      console.log('Selected files:', files.length, files.map(f => f.name));
      
      if (files.length === 0) {
        isProcessingImages.current = false;
        return; // Dosya seçilmediyse işlem yapma
      }
      
      // Dosya boyutlarını kontrol et (aynı dosyalar aynı boyutta olur)
      const fileSizes = files.map(f => f.size);
      console.log('File sizes:', fileSizes);
      
      const imageUrls = files.map(file => ({
        url: URL.createObjectURL(file),
        name: file.name,
        file: file,
        size: file.size, // Boyut bilgisini ekle
        id: Date.now() + Math.random() // Benzersiz ID ekle
      }));
      
      setFormData(prev => {
        console.log('Previous images count:', prev.images.length);
        
        // Aynı dosya adı VE boyutuna sahip görselleri filtrele
        const existingFiles = prev.images.map(img => `${img.name}-${img.size || 0}`);
        const newImageUrls = imageUrls.filter(img => {
          const fileKey = `${img.name}-${img.size}`;
          const isDuplicate = existingFiles.includes(fileKey);
          console.log(`Checking ${img.name}: ${isDuplicate ? 'DUPLICATE' : 'NEW'}`);
          return !isDuplicate;
        });
        
        console.log('New unique images count:', newImageUrls.length);
        const newImages = [...prev.images, ...newImageUrls];
        console.log('Final images count:', newImages.length);
        
        // İşlem tamamlandı, flag'i sıfırla
        setTimeout(() => {
          isProcessingImages.current = false;
        }, 100);
        
        return {
          ...prev,
          images: newImages
        };
      });
      
      // Input'u temizle ki aynı dosyalar tekrar eklenmesin
      e.target.value = '';
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => {
      const imageToRemove = prev.images[index];
      // Blob URL'yi temizle
      if (imageToRemove && imageToRemove.url && imageToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(imageToRemove.url);
      }
      
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      };
    });
  };

  const resetForm = () => {
    // Önceki görsellerin blob URL'lerini temizle
    formData.images.forEach(img => {
      if (img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
    
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      categories: [],
      subcategory: '',
      brand: '',
      sku: '',
      stock: '',
      min_stock: '5',
      images: [],
      specifications: [],
      tags: [],
      is_active: true,
      is_featured: false,
      weight: '',
      dimensions_length: '',
      dimensions_width: '',
      dimensions_height: '',
      shipping_free: false,
      shipping_cost: '0',
      estimated_delivery: '3-5 iş günü'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      // Görsel dosyalarını ayır
      const imageFiles = formData.images.filter(img => img.file).map(img => img.file);
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock),
        min_stock: parseInt(formData.min_stock),
        weight: formData.weight ? parseFloat(formData.weight) : null,
        dimensions_length: formData.dimensions_length ? parseFloat(formData.dimensions_length) : null,
        dimensions_width: formData.dimensions_width ? parseFloat(formData.dimensions_width) : null,
        dimensions_height: formData.dimensions_height ? parseFloat(formData.dimensions_height) : null,
        shipping_cost: parseFloat(formData.shipping_cost)
        // images alanını kaldırdık - mevcut resimleri korumak için
      };

      let createdProduct;
      if (editingProduct) {
        createdProduct = await productsAPI.updateProduct(editingProduct.id, productData);
        toast.success('Ürün güncellendi');
      } else {
        createdProduct = await productsAPI.createProduct(productData);
        toast.success('Ürün eklendi');
      }

      // Görselleri yükle
      if (imageFiles.length > 0 && createdProduct.data?.product?.id) {
        const formDataImages = new FormData();
        imageFiles.forEach(file => {
          formDataImages.append('images', file);
        });
        
        console.log('Uploading images for product:', createdProduct.data.product.id);
        console.log('Image files count:', imageFiles.length);
        
        await productsAPI.uploadImages(createdProduct.data.product.id, formDataImages);
        toast.success('Görseller yüklendi');
      }

      setShowAddForm(false);
      setEditingProduct(null);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Ürün kaydedilemedi:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Ürün kaydedilemedi';
      const errorDetails = error.response?.data?.errors || [];
      
      if (errorDetails.length > 0) {
        toast.error(`${errorMessage}: ${errorDetails.map(e => e.msg).join(', ')}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    
    // Mevcut görselleri doğru formatta hazırla - sadece gerçek dosya URL'lerini al
    const existingImages = (product.images || [])
      .filter(img => img.url && !img.url.startsWith('blob:')) // Blob URL'lerini filtrele
      .map(img => ({
        url: img.url,
        name: img.filename || img.originalName || 'Mevcut görsel',
        file: null // Mevcut görseller için file yok
      }));
    
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      original_price: product.original_price ? product.original_price.toString() : '',
      categories: product.categories || (product.category ? [product.category] : []),
      subcategory: product.subcategory || '',
      brand: product.brand,
      sku: product.sku,
      stock: product.stock.toString(),
      min_stock: product.min_stock.toString(),
      images: existingImages,
      specifications: product.specifications || [],
      tags: product.tags || [],
      is_active: product.is_active,
      is_featured: product.is_featured,
      weight: product.weight ? product.weight.toString() : '',
      dimensions_length: product.dimensions_length ? product.dimensions_length.toString() : '',
      dimensions_width: product.dimensions_width ? product.dimensions_width.toString() : '',
      dimensions_height: product.dimensions_height ? product.dimensions_height.toString() : '',
      shipping_free: product.shipping_free,
      shipping_cost: product.shipping_cost.toString(),
      estimated_delivery: product.estimated_delivery
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await productsAPI.deleteProduct(productId);
        toast.success('Ürün silindi');
        loadProducts();
      } catch (error) {
        console.error('Ürün silinemedi:', error);
        toast.error('Ürün silinemedi');
      }
    }
  };

  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
  };

  const clearSearch = () => {
    setSearchInput('');
    searchInputRef.current?.focus();
  };

  const clearFilters = () => {
    setSearchInput('');
    setCategoryFilter('');
    setStatusFilter('');
    setStockFilter('');
    setCurrentPage(1);
    searchInputRef.current?.focus();
  };

  const hasActiveFilters =
    debouncedSearch || categoryFilter || statusFilter || stockFilter;

  // Sayfa değiştirme fonksiyonu
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
    setCurrentPage(1);
  };

  if (initialLoading) {
    return (
      <div className="mx-auto max-w-7xl space-y-6 animate-pulse">
        <div className="h-10 w-64 rounded-lg bg-slate-200" />
        <div className="h-24 rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" />
        <div className="h-96 rounded-2xl bg-white shadow-sm ring-1 ring-slate-200" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ürün Yönetimi
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Toplam {totalProducts} ürün · İsim, marka veya SKU ile arayın
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700"
          >
            <Plus className="h-5 w-5" />
            Yeni Ürün Ekle
          </button>
        </div>

        {/* Ürün Ekleme/Düzenleme Formu */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Adı *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marka *
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategoriler * (Birden fazla seçebilirsiniz)
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {categories.map(category => (
                      <label key={category} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="categories"
                          value={category}
                          checked={formData.categories.includes(category)}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{category}</span>
                      </label>
                    ))}
                  </div>
                  {formData.categories.length === 0 && (
                    <p className="text-red-500 text-sm mt-1">En az bir kategori seçmelisiniz</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alt Kategori
                  </label>
                  <input
                    type="text"
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ürün Görselleri
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      name="images"
                      multiple
                      accept="image/*"
                      onChange={handleInputChange}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span className="text-sm text-gray-600">Görsel yüklemek için tıklayın</span>
                      <span className="text-xs text-gray-400">PNG, JPG, JPEG formatları desteklenir</span>
                    </label>
                  </div>
                  
                  {formData.images.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Yüklenen Görseller ({formData.images.length})
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {formData.images.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image.url.startsWith('http') || image.url.startsWith('blob:') ? image.url : `${image.url}`}
                              alt={`Ürün görseli ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border"
                              crossOrigin="anonymous"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'block';
                              }}
                            />
                            <div className="w-full h-24 bg-gray-200 rounded-lg border flex items-center justify-center text-gray-500 text-sm" style={{display: 'none'}}>
                              Resim Yüklenemedi
                            </div>
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                            {image.file ? (
                              <div className="absolute bottom-0 left-0 right-0 bg-green-500 text-white text-xs p-1 text-center">
                                Yeni
                              </div>
                            ) : (
                              <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs p-1 text-center">
                                Mevcut
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SKU *
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fiyat (₺) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orijinal Fiyat (₺)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stok *
                  </label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Aktif
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Öne Çıkan
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="shipping_free"
                    checked={formData.shipping_free}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Ücretsiz Kargo
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  İptal
                </button>
                <LoadingButton
                  type="submit"
                  loading={submitting}
                  className="px-6 py-2"
                >
                  {editingProduct ? 'Güncelle' : 'Ekle'}
                </LoadingButton>
              </div>
            </form>
          </div>
        )}

        {/* Arama ve Filtreler */}
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-4 flex items-center gap-2 text-sm font-medium text-slate-700">
            <Filter className="h-4 w-4 text-orange-500" />
            Arama ve filtreler
          </div>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label htmlFor="product-search" className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Hızlı arama
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  id="product-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Ürün adı, marka veya SKU..."
                  value={searchInput}
                  onChange={handleSearchInput}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-orange-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                    aria-label="Aramayı temizle"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:w-auto lg:min-w-[32rem]">
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Kategori
                </label>
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                >
                  <option value="">Tümü</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Durum
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                >
                  <option value="">Tümü</option>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Stok
                </label>
                <select
                  value={stockFilter}
                  onChange={(e) => {
                    setStockFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                >
                  <option value="">Tümü</option>
                  <option value="inStock">Stokta</option>
                  <option value="lowStock">Düşük stok</option>
                  <option value="outOfStock">Tükendi</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500">
                  Sırala
                </label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  >
                    <option value="created_at">En yeni</option>
                    <option value="name">İsim</option>
                    <option value="price">Fiyat</option>
                    <option value="stock">Stok</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                      setCurrentPage(1);
                    }}
                    className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    title={sortOrder === 'ASC' ? 'Artan' : 'Azalan'}
                  >
                    {sortOrder === 'ASC' ? 'A→Z' : 'Z→A'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4 text-sm">
            <span className="text-slate-600">
              {listLoading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-orange-500" />
                  Güncelleniyor...
                </span>
              ) : debouncedSearch ? (
                <>
                  <strong className="text-slate-900">"{debouncedSearch}"</strong> için {totalProducts} sonuç
                </>
              ) : (
                <>Listelenen: {products.length} / {totalProducts}</>
              )}
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm font-medium text-orange-600 hover:text-orange-700"
              >
                Filtreleri temizle
              </button>
            )}
          </div>
        </div>

        {/* Ürün Listesi */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h3 className="text-base font-semibold text-slate-900">Ürün listesi</h3>
            {listLoading && (
              <Loader2 className="h-5 w-5 animate-spin text-orange-500" aria-hidden />
            )}
          </div>
          
          {products.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Henüz ürün eklenmemiş.
            </div>
          ) : (
            <div className={`p-4 transition-opacity ${listLoading ? 'opacity-60' : ''}`}>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Ürün
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      SKU
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Kategori
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Fiyat
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Stok
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Durum
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {products.length > 0 ? products.map((product) => (
                    <tr key={product.id} className="transition-colors hover:bg-orange-50/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-slate-100 ring-1 ring-slate-200">
                            {product.images && product.images.length > 0 ? (
                              <img
                                className="h-full w-full object-cover"
                                src={getImageUrl(product.images[0].url)}
                                alt=""
                                crossOrigin="anonymous"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-400">
                                —
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 max-w-xs">
                            <div className="truncate text-sm font-medium text-slate-900" title={product.name}>
                              {product.name}
                            </div>
                            <div className="truncate text-xs text-slate-500">{product.brand}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-800">
                          {product.sku || '—'}
                        </code>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {product.categories ? (
                          <div className="flex flex-wrap gap-1">
                            {product.categories.map((category, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {category}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">Kategori yok</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        ₺{product.price.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {product.stock}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          product.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {product.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() => handleEdit(product)}
                            className="rounded-lg p-2 text-slate-600 transition hover:bg-orange-100 hover:text-orange-700"
                            title="Düzenle"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(product.id)}
                            className="rounded-lg p-2 text-slate-600 transition hover:bg-red-100 hover:text-red-700"
                            title="Sil"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                        Ürün bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Sayfa {currentPage} / {totalPages} (Toplam {totalProducts} ürün)
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      
                      {/* Sayfa numaraları */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 border rounded-lg ${
                              currentPage === pageNum
                                ? 'border-orange-500 bg-orange-500 text-white'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
    </div>
  );
};

export default AdminProducts;

